import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  SlidersHorizontal,
  Check,
  X,
  CalendarClock,
  ShieldCheck,
} from "lucide-react";

const PolicyActionsDropdown = ({ onApply, onReset, dropUp = false }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({ active: true, inactive: false });
  const [category, setCategory] = useState("all");
  const [type, setType] = useState("all");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleReset = () => {
    setStatus({ active: true, inactive: false });
    setCategory("all");
    setType("all");
    onReset && onReset();
  };

  const handleApply = () => {
    onApply && onApply({ status, category, type });
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-md hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-400/50"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <SlidersHorizontal size={16} />
        <span>Filter</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: dropUp ? 10 : -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: dropUp ? 10 : -10, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={`absolute ${
              dropUp ? "bottom-12" : "top-12"
            } -right-6 translate-x-2 origin-top-right z-30 w-80 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl`}
            role="menu"
          >
            <div className="p-4">
              <div className="flex gap-2 items-center">
                <div className="grid place-items-center w-9 h-9 text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg">
                  <ShieldCheck size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Policies filter
                  </p>
                  <p className="text-xs text-gray-500">
                    Narrow results by status and coverage
                  </p>
                </div>
              </div>

              <div className="mt-4 w-full h-px bg-gradient-to-r from-transparent via-gray-100 to-transparent" />

              {/* Status */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-700">Status</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { key: "active", label: "Active" },
                    { key: "inactive", label: "Inactive" },
                  ].map(({ key, label }) => {
                    const checked = status[key];
                    return (
                      <button
                        key={key}
                        onClick={() =>
                          setStatus((s) => ({ ...s, [key]: !s[key] }))
                        }
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          checked
                            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span>{label}</span>
                        {checked && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Type */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-700">Type</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    "all",
                    "Daily commuter",
                    "Biker/Okada", 
                    "Intercity travels",
                    "Trotro, Taxi or Bolt"
                  ].map((opt) => {
                    const selected = type === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setType(opt)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-blue-700 bg-blue-50 border-blue-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{opt}</span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-700">
                  Policy category
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    "all",
                    "1 day cover",
                    "2 days cover",
                    "3 days cover",
                    "7 days cover",
                  ].map((opt) => {
                    const selected = category === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setCategory(opt)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                          selected
                            ? "text-orange-700 bg-orange-50 border-orange-200"
                            : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span className="flex gap-2 items-center">
                          <CalendarClock size={14} className="text-gray-500" />
                          {opt}
                        </span>
                        {selected && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex justify-between items-center mt-5">
                <button
                  onClick={handleReset}
                  className="inline-flex gap-2 items-center px-3 py-2 text-xs font-semibold text-gray-600 rounded-full hover:text-gray-800 hover:bg-gray-50"
                >
                  <X size={14} />
                  Reset
                </button>
                <button
                  onClick={handleApply}
                  className="inline-flex gap-2 items-center px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-700 rounded-full shadow-md hover:from-emerald-700 hover:to-green-800"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PolicyActionsDropdown;
